/**
 * Validación del formulario de inspección (entrega y devolución).
 *
 * DOS COSAS QUE ESTE ARCHIVO APRENDIÓ A LA MALA
 *
 * 1. LA LISTA DE ÁREAS ESTABA ESCRITA DOS VECES. Acá había quince literales
 *    copiados a mano y en `lib/types/inspection.types.ts` estaba la lista real.
 *    El día que la entrega sumó la foto de la licencia --un área nueva-- esta
 *    copia no se enteró, así que `safeParse` rechazaba el formulario entero por
 *    un `enum` inválido. El efecto en la calle: el botón de "Confirmar entrega"
 *    se veía HABILITADO, la persona lo tocaba y no pasaba absolutamente nada
 *    visible, porque el error caía bajo la llave `photos`, que no tenía dónde
 *    mostrarse. Una entrega imposible de enviar y sin un solo mensaje.
 *    Ahora las áreas se DERIVAN de la única fuente de verdad. Agregar un área
 *    no puede volver a desincronizar nada.
 *
 * 2. LOS MENSAJES SALÍAN EN INGLÉS. "At least 15 live photos required" se le
 *    mostraba tal cual a alguien parado junto a un carro en San Salvador. Los
 *    mensajes de acá son LLAVES de i18n, y quien los muestra los pasa por
 *    `t()`. Un esquema no puede traducir --no tiene contexto de idioma-- pero sí
 *    puede nombrar el problema en vez de redactarlo.
 *
 * LA CUENTA DE FOTOS EXCLUYE LA LICENCIA, y eso es una regla, no un detalle: la
 * licencia comparte el arreglo `photos` porque reusa la misma cámara y la misma
 * subida, pero no es una foto del carro. Contarla dejaría pasar una entrega con
 * catorce ángulos del vehículo y el documento haciendo de decimoquinto.
 */
import { z } from 'zod'
import {
  INSPECTION_PHOTO_AREAS,
  LICENSE_PHOTO_AREA,
  faltanAreasObligatorias,
} from '../types/inspection.types'

const conditionGradeSchema = z.enum(['excellent', 'good', 'fair', 'poor'], {
  message: 'inspection.invalid.condition',
})

/** Las quince del carro más la licencia — derivadas, nunca copiadas. */
const PHOTO_AREAS = [...INSPECTION_PHOTO_AREAS, LICENSE_PHOTO_AREA] as const

const inspectionPhotoSchema = z.object({
  uri: z.string().min(1, 'inspection.invalid.photo_uri'),
  area: z.enum(PHOTO_AREAS, { message: 'inspection.invalid.photo_area' }),
  width: z.number().int().positive('inspection.invalid.photo_size'),
  height: z.number().int().positive('inspection.invalid.photo_size'),
  capturedAt: z.string().datetime({ message: 'inspection.invalid.photo_captured_at' }),
  source: z.literal('tago_live_camera', { message: 'inspection.invalid.photo_source' }),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  locationAccuracyMeters: z.number().optional(),
  /**
   * La marca de daño viaja con la foto. Sin declararla acá, Zod la descartaba
   * del objeto validado — y aunque el servicio lea el original, un esquema que
   * no conoce un campo es un esquema que miente sobre la forma del dato.
   */
  isDamage: z.boolean().optional(),
})

export const inspectionFormSchema = z.object({
  fuelLevel: z
    .number()
    .min(0, 'inspection.invalid.fuel_range')
    .max(100, 'inspection.invalid.fuel_range')
    .int('inspection.invalid.fuel_range'),
  odometerKm: z
    .number()
    .min(0, 'inspection.invalid.odometer_range')
    .max(999999, 'inspection.invalid.odometer_range')
    .int('inspection.invalid.odometer_whole'),
  exteriorCondition: conditionGradeSchema,
  interiorCondition: conditionGradeSchema,
  notes: z.string().max(500, 'inspection.invalid.notes_long').default(''),
  photos: z.array(inspectionPhotoSchema).superRefine((photos, ctx) => {
    const delCarro = photos.filter((p) => p.area !== LICENSE_PHOTO_AREA)
    /*
     * Se comprueba el CONJUNTO, no la cuenta.
     *
     * Mientras las obligatorias fueron las quince areas, `length >= 15` y
     * "estan todas" eran la misma condicion. Al bajar a seis dejaron de serlo:
     * contar sola aceptaria una entrega con seis fotos de asientos y cero de la
     * carroceria. La cuenta quedo para la barra de progreso.
     */
    const faltantes = faltanAreasObligatorias(delCarro.map((p) => p.area))
    if (faltantes.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'inspection.invalid.photos_min',
        params: { faltantes },
      })
    }
    // Un área repetida significa dos fotos del mismo ángulo: la base ya lo
    // impide con un índice único, pero decirlo acá evita subir megas para que
    // el servidor rechace al final.
    const areas = new Set(photos.map((p) => p.area))
    if (areas.size !== photos.length) {
      ctx.addIssue({ code: 'custom', message: 'inspection.invalid.photos_duplicated' })
    }
  }),
  /**
   * Opcionales acá y obligatorias en la pantalla, a propósito: un reintento de
   * subida de fotos no puede morir por un campo que no cambió, y hay
   * inspecciones sembradas que no las traen.
   */
  guestSignatureSvg: z.string().nullable().optional(),
  hostSignatureSvg: z.string().nullable().optional(),
})
